import { Layers } from "lucide-react";
import type { LayerStats, LayerValue } from "../types/api";

/**
 * Display label for each categorical layer code. Add a line here when a new
 * classification layer is ingested. The first segment is the scheme (IPCC,
 * CFT, …); the card groups by source visually but keeps a flat list so
 * adding a new scheme is one entry, not a new section.
 */
const IPCC_DISPLAY: Record<string, string> = {
  ipcc_climate: "IPCC Climate",
  ipcc_soil:    "IPCC Soil",
  cft_climate:  "CFT Climate",
  cft_soil:     "CFT Soil",
};

type IpccItem = {
  code: string;
  /** Human-readable axis label, e.g. "Climate" / "Soil". */
  axis: string;
  /** Integer class ID (`null` when the pixel is nodata). */
  value: number | null;
  /** Class label, e.g. "Tropical Wet". `null` when the pixel is nodata. */
  label: string | null;
};

function isCategoricalPoint(r: LayerValue): boolean {
  return r.label != null;
}

function isCategoricalZone(r: LayerStats): boolean {
  return r.mode_label != null;
}

/** Pull the categorical entries out of mixed soil/weather/climate arrays. */
export function selectIpccPoint(
  climate: LayerValue[] | undefined,
  soil:    LayerValue[] | undefined,
): IpccItem[] {
  const fromClimate = (climate ?? []).map((r) => toIpccItem(r.code, r.value, r.label));
  const fromSoil    = (soil ?? []).filter(isCategoricalPoint).map((r) => toIpccItem(r.code, r.value, r.label));
  return [...fromClimate, ...fromSoil];
}

export function selectIpccZone(
  climate: LayerStats[] | undefined,
  soil:    LayerStats[] | undefined,
): IpccItem[] {
  const fromClimate = (climate ?? []).map((r) => toIpccItem(r.code, r.mode ?? null, r.mode_label ?? null));
  const fromSoil    = (soil ?? []).filter(isCategoricalZone)
    .map((r) => toIpccItem(r.code, r.mode ?? null, r.mode_label ?? null));
  return [...fromClimate, ...fromSoil];
}

function toIpccItem(code: string, value: number | null, label: string | null | undefined): IpccItem {
  return {
    code,
    axis: IPCC_DISPLAY[code] ?? code,
    value,
    label: label ?? null,
  };
}

export function IpccCard({ items, modal = false }: { items: IpccItem[]; modal?: boolean }) {
  if (!items.length) return null;
  return (
    <div className="rounded-xs border-[1.2px] border-mef-separator bg-white p-4 shadow-mef-card">
      <div className="mb-3 flex items-center gap-2">
        <Layers className="h-4 w-4 text-mef-orange" />
        <h3 className="mef-caption">
          Classifications{modal && " — dominant in zone"}
        </h3>
      </div>
      <dl className="space-y-2">
        {items.map((it) => (
          <div key={it.code} className="flex items-baseline justify-between gap-3">
            <dt className="text-[11px] uppercase tracking-[0.04em] text-black/40">{it.axis}</dt>
            <dd className="flex items-baseline gap-2">
              {it.label ? (
                <span className="text-[14px] font-medium leading-5 text-black/80">{it.label}</span>
              ) : (
                <span className="text-[13px] text-black/40">—</span>
              )}
              {it.value != null && (
                <span className="font-mono text-[10px] tabular-nums text-black/40">
                  [{Math.round(it.value)}]
                </span>
              )}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
